import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Container, Col, Row, Card, CardTitle } from 'reactstrap';
import { useBoolean } from 'usehooks-ts';

import images from 'assets/images';
import { LOCATIONS } from 'constants/index';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppMeta from 'components/App/Meta';
import AppButton from 'components/App/Button';
import AppAvatar from 'components/App/Avatar';

import CardLocationContactInfo from 'components/CardLocationContactInfo';
import ModalAddContactInfo from 'components/Modals/ModalAddContactInfo';
import ModalViewContactInfo from 'components/Modals/ModalViewContactInfo';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Loading from 'components/App/Loading';

import { ReactComponent as Add } from 'assets/images/Add.svg';
import { ReactComponent as At } from 'assets/images/At.svg';
import { ReactComponent as Phone } from 'assets/images/Phone.svg';
import { ReactComponent as Language } from 'assets/images/Language.svg';
import Avatar from 'assets/images/Avatar.png';

import { showSuccess } from 'utils';
import styles from './ContactInfo.module.scss';

const breadcrumbList = [
  {
    name: 'Chỗ nghỉ',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Thông tin liên hệ',
    isActive: true,
    link: '',
  },
];

function ContactInfo(props) {
  const {
    isLoadingGetPlaceContacts,
    isLoadingGetPlaceContactDetails,

    setting,

    placeContactsList,
    placeContactDetails,

    actionGetPlaceContacts,
    actionGetPlaceContactDetails,
    actionAddPlaceContact,
    actionEditPlaceContact,
    actionDeletePlaceContact,
    actionGetSetting,
  } = props;

  const getGroupChildren = React.useCallback(
    (group) =>
      placeContactsList
        .filter((item) => item.group === group)
        .flatMap((item) => item.children),
    [placeContactsList],
  );

  const {
    value: isOpenModal,
    setFalse: onCloseModal,
    setTrue: onOpenModal,
    toggle: onToggleModal,
  } = useBoolean(false);

  const {
    value: isOpenModalDeletePlaceContact,
    setFalse: onCloseModalDeletePlaceContact,
    setTrue: onOpenModalDeletePlaceContact,
    toggle: onToggleModalDeletePlaceContact,
  } = useBoolean(false);

  const {
    value: isOpenModalViewContactInfo,
    setFalse: onCloseModalViewContactInfo,
    setTrue: onOpenModalViewContactInfo,
  } = useBoolean(false);

  const [contactGroup, setContactGroup] = React.useState('');
  const [modalType, setModalType] = React.useState('');

  const { search } = useLocation();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
    };
  }, [search]);

  const onToggleModalAddContactInfo = React.useCallback(
    (e) => {
      onOpenModal();
      setContactGroup(e);
      setModalType('ADD');
    },
    [onOpenModal],
  );

  const onCallBackDeletePlaceContact = React.useCallback(() => {
    showSuccess('Xoá liên hệ thành công');
    actionGetPlaceContacts({ place: searchQuery.place });
    onCloseModal();
    onCloseModalDeletePlaceContact();
    onCloseModalViewContactInfo();
  }, [
    actionGetPlaceContacts,
    onCloseModal,
    onCloseModalDeletePlaceContact,
    onCloseModalViewContactInfo,
    searchQuery.place,
  ]);

  const onClickConfirmDeletePlaceContact = React.useCallback(() => {
    actionDeletePlaceContact({
      id: placeContactDetails._id,
      place: searchQuery.place,
      callBack: onCallBackDeletePlaceContact,
    });
  }, [
    actionDeletePlaceContact,
    onCallBackDeletePlaceContact,
    placeContactDetails._id,
    searchQuery.place,
  ]);

  const onOpenDeleteFromViewContact = React.useCallback(() => {
    onOpenModalDeletePlaceContact();
  }, [onOpenModalDeletePlaceContact]);

  const onOpenEditFromViewContact = React.useCallback(() => {
    if (placeContactDetails?.group) {
      setContactGroup(placeContactDetails.group);
    }
    setModalType('EDIT');
    onCloseModalViewContactInfo();
    onOpenModal();
    actionGetPlaceContactDetails({
      id: placeContactDetails._id,
      place: searchQuery.place,
    });
  }, [
    actionGetPlaceContactDetails,
    onCloseModalViewContactInfo,
    onOpenModal,
    placeContactDetails,
    searchQuery.place,
  ]);

  const onOpenViewContactInfo = React.useCallback(() => {
    onOpenModalViewContactInfo();
  }, [onOpenModalViewContactInfo]);

  const onAddContactSuccess = React.useCallback((createdContact) => {
    if (!createdContact) return;
    actionGetPlaceContactDetails({
      id: createdContact,
      place: searchQuery.place,
    });
    onOpenModalViewContactInfo();
  }, [actionGetPlaceContactDetails, onOpenModalViewContactInfo, searchQuery.place]);

  React.useEffect(() => {
    actionGetPlaceContacts(searchQuery);
    actionGetSetting();
  }, [actionGetPlaceContacts, actionGetSetting, searchQuery]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta name="Thông tin liên hệ" />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="Thông tin liên hệ"
          breadcrumbList={breadcrumbList}
        />

        <Loading loading={isLoadingGetPlaceContacts} loaderOnPage />

        <Card className="Section__Card">
          <CardTitle className="Section__Title">
            Thông tin liên hệ của Bookese
          </CardTitle>

          <div className="BoxAvatar BoxAvatar__ContactInfo">
            <div className="BoxAvatar__ContactInfo__Left">
              <AppAvatar className="avatar-select" src={Avatar} />

              <div className="Text">
                <div className="Text__Title">HUY TRUONG</div>

                <div className="Text__Subtitle">Founder Bookese</div>
              </div>
            </div>

            <div className="BoxAvatar__ContactInfo__Right">
              <div className="Right__Item">
                <At />
                <p>huy.truong@bookese.com </p>
              </div>

              <div className="Right__Item">
                <Phone />
                <p>093 888 4348</p>
              </div>

              <div className="Right__Item">
                <Language />
                <p>Tiếng Việt, Tiếng Anh</p>
              </div>
            </div>
          </div>
        </Card>

        <Row>
          <Col md="6" xxl="3">
            <Card className="Section">
              <CardTitle className="Section__Title">
                Đại diện liên hệ chính
              </CardTitle>

              <p className="Section__Subtitle">
                Đây là chi tiết liên hệ được gửi cho khách trong xác nhận đặt
                phòng
              </p>

              {getGroupChildren('MAIN').map((item) => (
                <CardLocationContactInfo
                  key={item._id}
                  list={item}
                  placeId={searchQuery.place}
                  setModalType={setModalType}
                  onOpenModal={onOpenModal}
                  onOpenModalViewContactInfo={onOpenViewContactInfo}
                  actionGetPlaceContactDetails={actionGetPlaceContactDetails}
                />
              ))}

              <AppButton
                color="secondary"
                classes="BtnAddRepresentative"
                value="MAIN"
                onClick={onToggleModalAddContactInfo}
              >
                <Add />

                <p className="TextButton">Thêm người đại diện liên hệ</p>
              </AppButton>
            </Card>
          </Col>

          <Col md="6" xxl="3">
            <Card className="Section">
              <CardTitle className="Section__Title">Đặt phòng</CardTitle>

              <p className="Section__Subtitle">
                Đây là đại điện liên hệ và quản lý các đặt phòng của Quý vị
              </p>

              {getGroupChildren('BOOKING').map((item) => (
                <CardLocationContactInfo
                  key={item._id}
                  list={item}
                  placeId={searchQuery.place}
                  setModalType={setModalType}
                  onOpenModal={onOpenModal}
                  onOpenModalViewContactInfo={onOpenViewContactInfo}
                  actionGetPlaceContactDetails={actionGetPlaceContactDetails}
                />
              ))}

              <AppButton
                color="secondary"
                classes="BtnAddRepresentative"
                value="BOOKING"
                onClick={onToggleModalAddContactInfo}
              >
                <Add />

                <p className="TextButton">Thêm người đại diện liên hệ</p>
              </AppButton>
            </Card>
          </Col>

          <Col md="6" xxl="3">
            <Card className="Section">
              <CardTitle className="Section__Title">
                Thông tin & hỗ trợ
              </CardTitle>

              <p className="Section__Subtitle">
                Đây là chi tiết liên hệ hỗ trợ khách hàng
              </p>

              {getGroupChildren('SUPPORT').map((item) => (
                <CardLocationContactInfo
                  key={item._id}
                  list={item}
                  placeId={searchQuery.place}
                  setModalType={setModalType}
                  onOpenModal={onOpenModal}
                  onOpenModalViewContactInfo={onOpenViewContactInfo}
                  actionGetPlaceContactDetails={actionGetPlaceContactDetails}
                />
              ))}

              <AppButton
                color="secondary"
                classes="BtnAddRepresentative"
                value="SUPPORT"
                onClick={onToggleModalAddContactInfo}
              >
                <Add />

                <p className="TextButton">Thêm người đại diện liên hệ</p>
              </AppButton>
            </Card>
          </Col>

          <Col md="6" xxl="3">
            <Card className="Section">
              <CardTitle className="Section__Title">
                Thanh toán & hóa đơn
              </CardTitle>

              <p className="Section__Subtitle">
                Đây là chi tiết liên hệ hỗ trợ khách thanh toán và gửi hoá đơn
                thanh toán về Email này
              </p>

              {getGroupChildren('PAYMENT').map((item) => (
                <CardLocationContactInfo
                  key={item._id}
                  list={item}
                  placeId={searchQuery.place}
                  setModalType={setModalType}
                  onOpenModal={onOpenModal}
                  onOpenModalViewContactInfo={onOpenViewContactInfo}
                  actionGetPlaceContactDetails={actionGetPlaceContactDetails}
                />
              ))}

              <AppButton
                color="secondary"
                classes="BtnAddRepresentative"
                value="PAYMENT"
                onClick={onToggleModalAddContactInfo}
              >
                <Add />

                <p className="TextButton">Thêm người đại diện liên hệ</p>
              </AppButton>
            </Card>
          </Col>
        </Row>

        <ModalAddContactInfo
          modalType={modalType}
          placeId={searchQuery.place || ''}
          isOpen={isOpenModal}
          isLoadingGetPlaceContactDetails={isLoadingGetPlaceContactDetails}
          placeContactDetails={placeContactDetails}
          contactGroup={contactGroup}
          languageSettings={setting.languages}
          roleSettings={setting.roles}
          onToggleModal={onToggleModal}
          onCloseModal={onCloseModal}
          actionGetPlaceContacts={actionGetPlaceContacts}
          actionAddPlaceContact={actionAddPlaceContact}
          actionEditPlaceContact={actionEditPlaceContact}
          onOpenModalDeletePlaceContact={onOpenModalDeletePlaceContact}
          onAddSuccess={onAddContactSuccess}
        />

        <ModalRequestAction
          isOpen={isOpenModalDeletePlaceContact}
          icon={images.modalGarbageCan.default}
          textRequest={'Bạn có chắc chắn muốn xoá \n thông tin liên hệ này không?'}
          onToggle={onToggleModalDeletePlaceContact}
          onCloseModal={onCloseModalDeletePlaceContact}
          onConfirm={onClickConfirmDeletePlaceContact}
        />

        <ModalViewContactInfo
          isOpen={isOpenModalViewContactInfo}
          onCloseModal={onCloseModalViewContactInfo}
          placeContactDetails={placeContactDetails}
          onOpenDeleteModal={onOpenDeleteFromViewContact}
          onOpenEditModal={onOpenEditFromViewContact}
        />
      </Container>
    </div>
  );
}

ContactInfo.propTypes = {
  isLoadingGetPlaceContacts: PropTypes.bool.isRequired,
  isLoadingGetPlaceContactDetails: PropTypes.bool.isRequired,

  setting: PropTypes.instanceOf(Object).isRequired,

  placeContactsList: PropTypes.instanceOf(Array).isRequired,
  placeContactDetails: PropTypes.instanceOf(Object).isRequired,

  actionGetPlaceContacts: PropTypes.func.isRequired,
  actionGetPlaceContactDetails: PropTypes.func.isRequired,
  actionAddPlaceContact: PropTypes.func.isRequired,
  actionEditPlaceContact: PropTypes.func.isRequired,
  actionDeletePlaceContact: PropTypes.func.isRequired,
  actionGetSetting: PropTypes.func.isRequired,
};

ContactInfo.defaultProps = {};

export default memo(ContactInfo);
